import React from "react"
import { useTranslation } from "react-i18next"
import {
  ProductAssociationApplyPopup,
  type ProductAssociationLabels,
  type ProductAssociationRequest,
} from "../../../components/Associations/ProductAssociationApplyPopup"
import { EAdditionalApplyTargetType } from "../../../Enum/EAdditionalApplyTargetType"
import type { ApplyAdditionalRequest } from "../../../hooks/Addtional/useApplyAdditional"

interface AdditionalApplyPopupProps {
  title: string
  actionLabel: string
  helpTitle: string
  helpDescription: string
  helpBullets: string[]
  helpKey: string
  onClose: () => void
  onConfirm: (request: ApplyAdditionalRequest) => Promise<void>
}

export const AdditionalApplyPopup: React.FC<AdditionalApplyPopupProps> = ({ onConfirm, ...props }) => {
  const { t } = useTranslation("addtional")

  const labels: ProductAssociationLabels = {
    targetLegend: t("apply_target_legend"),
    allProducts: t("apply_target_all_products"),
    selectedProducts: t("apply_target_selected_products"),
    allMeals: t("apply_target_all_meals"),
    selectedMeals: t("apply_target_selected_meals"),
    allDrinks: t("apply_target_all_drinks"),
    selectedDrinks: t("apply_target_selected_drinks"),
    allCombos: t("apply_target_all_combos"),
    selectedCombos: t("apply_target_selected_combos"),
    selectMealsAction: t("apply_select_meals_action"),
    selectDrinksAction: t("apply_select_drinks_action"),
    selectCombosAction: t("apply_select_combos_action"),
    selectedMealsCount: t("apply_selected_meals"),
    selectedDrinksCount: t("apply_selected_drinks"),
    selectedCombosCount: t("apply_selected_combos"),
    selectedMealsTitle: t("apply_target_selected_meals"),
    selectedDrinksTitle: t("apply_target_selected_drinks"),
    selectedCombosTitle: t("apply_target_selected_combos"),
    noSelectedMeals: t("apply_no_selected_meals"),
    noSelectedDrinks: t("apply_no_selected_drinks"),
    noSelectedCombos: t("apply_no_selected_combos"),
  }

  const handleConfirm = (request: ProductAssociationRequest) =>
    onConfirm({ ...request, targetType: request.targetType as EAdditionalApplyTargetType })

  return (
    <ProductAssociationApplyPopup
      {...props}
      labels={labels}
      targetValues={{
        allProducts: EAdditionalApplyTargetType.AllProducts,
        selectedProducts: EAdditionalApplyTargetType.SelectedProducts,
        allMeals: EAdditionalApplyTargetType.AllMeals,
        selectedMeals: EAdditionalApplyTargetType.SelectedMeals,
        allDrinks: EAdditionalApplyTargetType.AllDrinks,
        selectedDrinks: EAdditionalApplyTargetType.SelectedDrinks,
        allCombos: EAdditionalApplyTargetType.AllCombos,
        selectedCombos: EAdditionalApplyTargetType.SelectedCombos,
      }}
      interactionKeys={{
        closeDescriptionKey: "interactionDescriptions:pages.products.addtional.additionalApplyPopup.button.onClose",
        targetDescriptionKey:
          "interactionDescriptions:pages.products.addtional.additionalApplyPopup.select.applyTargetLegend",
        confirmDescriptionKey: "interactionDescriptions:pages.products.addtional.additionalApplyPopup.button.actionLabel",
        addMealDescriptionKey: "interactionDescriptions:pages.products.addtional.additionalApplyPopup.button.addMeal",
        removeMealDescriptionKey:
          "interactionDescriptions:pages.products.addtional.additionalApplyPopup.button.removeMeal",
        addDrinkDescriptionKey: "interactionDescriptions:pages.products.addtional.additionalApplyPopup.button.addDrink",
        removeDrinkDescriptionKey:
          "interactionDescriptions:pages.products.addtional.additionalApplyPopup.button.removeDrink",
        addComboDescriptionKey: "interactionDescriptions:pages.products.addtional.additionalApplyPopup.button.addCombo",
        removeComboDescriptionKey:
          "interactionDescriptions:pages.products.addtional.additionalApplyPopup.button.removeCombo",
      }}
      onConfirm={handleConfirm}
    />
  )
}
